// The DB stores these as strings (validated by CHECK constraints); these unions
// are the single source of truth for valid values + display labels.

export const ROLES = ["ADMIN", "USER_ADMIN", "CONTRACTOR"] as const;
export type Role = (typeof ROLES)[number];

/**
 * The roles that sign in to the admin side (AUTH-13). A USER_ADMIN runs the
 * contractor directory, clients and projects, timesheet review and hours-only
 * reports — never money. Every route stays `["ADMIN"]` unless it is
 * deliberately opened with this list, so a new route is closed to a user admin
 * by default.
 */
export const STAFF_ROLES = ["ADMIN", "USER_ADMIN"] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/**
 * Whether a role is shown money: rates, values, budgets, spend (AUTH-13). The
 * one answer the API's stripping and the SPA's hiding both ask.
 */
export function seesMoney(role: Role): boolean {
  return role === "ADMIN";
}

export const ROLE_LABEL: Record<Role, string> = {
  ADMIN: "Admin",
  USER_ADMIN: "User admin",
  CONTRACTOR: "Contractor",
};

export function isStaffRole(role: Role): role is StaffRole {
  return (STAFF_ROLES as readonly Role[]).includes(role);
}

export const USER_STATUSES = ["ACTIVE", "INACTIVE", "INVITED"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  ACTIVE: "Active",
  INACTIVE: "Disabled",
  // Invited but not yet activated via Entra (S2.1/S2.2). Cannot sign in until
  // the invite is accepted, at which point the status becomes ACTIVE.
  INVITED: "Invited",
};

export const INVITE_STATUSES = ["PENDING", "ACCEPTED", "REVOKED"] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];

export const ENTRY_TYPES = [
  "WORKED",
  "ANNUAL_LEAVE",
  "SICK_LEAVE",
  "PUBLIC_HOLIDAY",
  "NON_WORKING",
] as const;
export type EntryType = (typeof ENTRY_TYPES)[number];

export const ENTRY_TYPE_LABEL: Record<EntryType, string> = {
  WORKED: "Worked",
  ANNUAL_LEAVE: "Annual leave",
  SICK_LEAVE: "Sick leave",
  PUBLIC_HOLIDAY: "Public holiday",
  NON_WORKING: "Non-working day",
};

/** Types that count as paid working hours toward the week total. */
export function isWorkedType(t: EntryType): boolean {
  return t === "WORKED";
}

export const TIMESHEET_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "CHANGES_REQUESTED",
] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];

/** Editor-facing status: "NEW" means no row exists yet for that week. */
export type EditorStatus = TimesheetStatus | "NEW";

export const TIMESHEET_STATUS_LABEL: Record<TimesheetStatus, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Pending",
  APPROVED: "Approved",
  CHANGES_REQUESTED: "Changes requested",
};

export const NOTIFICATION_TYPES = [
  "TIMESHEET_APPROVED",
  "CHANGES_REQUESTED",
  /** The monthly nudge for weeks that have come and gone unfiled. */
  "TIMESHEET_REMINDER",
  /**
   * An approval withdrawn inside the undo window — the week is back to pending
   * review, not back with the contractor.
   *
   * Deliberately not `CHANGES_REQUESTED`, which it used to borrow. Nothing was
   * requested and there is no note to act on, and that type's CTA reads "Fix
   * timesheet" — an instruction to edit a week the contractor cannot edit,
   * because it is SUBMITTED again.
   */
  "TIMESHEET_REOPENED",
  /**
   * Contractor invoices (CINV). Each is a fact about the invoice's workflow status,
   * told to the contractor who uploaded it. Adding a member here means widening
   * the `Notification_type_check` CHECK constraint in the same change — the DB
   * re-asserts this list (DATA-1), and a member it does not know about makes the
   * status change that writes it roll back.
   */
  "INVOICE_CORRECTION_REQUESTED",
  "INVOICE_APPROVED",
  "INVOICE_REJECTED",
  /** A payment that leaves something still to pay (CINV-23). Paid in full is INVOICE_PAID. */
  "INVOICE_PART_PAID",
  "INVOICE_PAID",
  /**
   * The first notification told to **admins**: a contractor invoice arrived or
   * was re-uploaded. The bell shows it at once; the email is not sent per row
   * but gathered into one end-of-day digest per admin (services/invoice-digest).
   */
  "INVOICE_SUBMITTED",
  /**
   * To admins: the approved hours under an approved or paid invoice fell after
   * it was approved (CINV-28). Gathered into the same end-of-day digest.
   */
  "INVOICE_CHANGED",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const PROJECT_STATUSES = ["ACTIVE", "ON_HOLD", "CLOSED"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  ACTIVE: "Active",
  ON_HOLD: "On hold",
  CLOSED: "Closed",
};

/**
 * The unit a rate was *agreed* in — presentation only. Hours are the canonical
 * stored unit (RATE-3), so a DAY-rated assignment stores the hourly equivalent
 * and renders as days. The unit is what tells the UI which way to render it.
 */
export const RATE_UNITS = ["DAY", "HOUR"] as const;
export type RateUnit = (typeof RATE_UNITS)[number];

export const RATE_UNIT_LABEL: Record<RateUnit, string> = {
  DAY: "day",
  HOUR: "hour",
};

/** One legal entity per currency (PRD decision 2): Australian and Indian. */
export const CURRENCIES = ["AUD", "INR"] as const;
export type Currency = (typeof CURRENCIES)[number];

/**
 * Sortable columns on the admin approval queue.
 *
 * The queue's unit is a contractor-week (one row can span several projects), so
 * these name row-level facts, not sheet-level ones. `contractor` and `client`
 * sort on joined names, which is why the queue reads through raw SQL rather than
 * Prisma's `groupBy` — `groupBy.orderBy` accepts only by-fields and aggregates.
 */
export const QUEUE_SORT_FIELDS = [
  "submittedAt",
  "weekStart",
  "contractor",
  "client",
  "totalHours",
  "status",
] as const;
export type QueueSortField = (typeof QUEUE_SORT_FIELDS)[number];

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export const REPORT_GROUP_BY = ["week", "client", "project", "contractor"] as const;
export type ReportGroupBy = (typeof REPORT_GROUP_BY)[number];

export const REPORT_GROUP_BY_LABEL: Record<ReportGroupBy, string> = {
  week: "Week",
  client: "Client",
  project: "Project",
  contractor: "Contractor",
};

/**
 * Which weeks a report counts.
 *
 * `APPROVED_AND_SUBMITTED` is the historical behaviour and stays the default so
 * no number moves silently. It does mix unapproved work into a money figure,
 * which is why `APPROVED` exists — INV-1 says an invoice is approved-only, so
 * anyone reconciling against billing wants that scope.
 */
export const REPORT_SCOPES = ["APPROVED", "APPROVED_AND_SUBMITTED"] as const;
export type ReportScope = (typeof REPORT_SCOPES)[number];

export const REPORT_SCOPE_LABEL: Record<ReportScope, string> = {
  APPROVED: "Approved only",
  APPROVED_AND_SUBMITTED: "Approved & pending",
};

// --- Contractor invoices (CINV) ----------------------------------------------
// The *incoming* invoice a contractor uploads for a month. Deliberately not
// `Invoice`/`INV-*`, which docs/design.md reserves for the outgoing invoices the
// business would issue to a client.

/**
 * The workflow status — what the contractor and the admin both see.
 *
 *   SUBMITTED           uploaded, awaiting an admin
 *   CHANGES_REQUESTED   returned with a note; the contractor re-uploads
 *   APPROVED            approved for payment
 *   PART_PAID           some of it paid — each payment its own row (CINV-23)
 *   PAID                paid in full, or closed short with a reason
 *   REJECTED            terminal — the month is free for a fresh upload
 *
 * The AI's verdict is a *separate* axis (`INVOICE_VALIDATION_STATUSES`) and never
 * moves this one: the engine advises, a human approves (CINV-6).
 */
export const CONTRACTOR_INVOICE_STATUSES = [
  "SUBMITTED",
  "CHANGES_REQUESTED",
  "APPROVED",
  "PART_PAID",
  "PAID",
  "REJECTED",
] as const;
export type ContractorInvoiceStatus = (typeof CONTRACTOR_INVOICE_STATUSES)[number];

export const CONTRACTOR_INVOICE_STATUS_LABEL: Record<ContractorInvoiceStatus, string> = {
  SUBMITTED: "Awaiting review",
  CHANGES_REQUESTED: "Correction requested",
  APPROVED: "Approved for payment",
  PART_PAID: "Part paid",
  PAID: "Paid",
  REJECTED: "Rejected",
};

/** Statuses that hold the contractor-month (CINV-1): anything but REJECTED. */
export const LIVE_CONTRACTOR_INVOICE_STATUSES: readonly ContractorInvoiceStatus[] = [
  "SUBMITTED",
  "CHANGES_REQUESTED",
  "APPROVED",
  "PART_PAID",
  "PAID",
];

/**
 * Where an admin may still file an invoice under projects — its own project and
 * its line tags (CINV-35): anything but REJECTED. The same set as the live one
 * today, for a different reason: a decided invoice's filing is reporting, not
 * money, while its fields freeze at the decision (CINV-7).
 */
export const FILEABLE_CONTRACTOR_INVOICE_STATUSES: readonly ContractorInvoiceStatus[] = [
  "SUBMITTED",
  "CHANGES_REQUESTED",
  "APPROVED",
  "PART_PAID",
  "PAID",
];

/**
 * Approved for payment, whether or not any of it is paid yet: what the previous
 * invoice's rate, the GST report and a project's figures count as decided.
 */
export const APPROVED_CONTRACTOR_INVOICE_STATUSES: readonly ContractorInvoiceStatus[] = [
  "APPROVED",
  "PART_PAID",
  "PAID",
];

/**
 * Where the AI read of the PDF stands. `NOT_CONFIGURED` is the deterministic
 * state on a dev box and in e2e — no OpenRouter key — so the manual-fields path
 * is always reachable and the UI never waits on a worker that will not run.
 */
export const INVOICE_EXTRACTION_STATUSES = [
  "PENDING",
  "DONE",
  "FAILED",
  "NOT_CONFIGURED",
] as const;
export type InvoiceExtractionStatus = (typeof INVOICE_EXTRACTION_STATUSES)[number];

/**
 * The engine's verdict (admin-only, CINV-15). Precedence when several checks
 * fire is fixed in `invoice-validation.ts` (CINV-9).
 */
export const INVOICE_VALIDATION_STATUSES = [
  "PENDING",
  "MATCHED",
  "MISMATCH",
  "NEEDS_REVIEW",
  "DUPLICATE",
  "MISSING_TIMESHEET",
  "EXTRACTION_FAILED",
] as const;
export type InvoiceValidationStatus = (typeof INVOICE_VALIDATION_STATUSES)[number];

export const INVOICE_VALIDATION_STATUS_LABEL: Record<InvoiceValidationStatus, string> = {
  PENDING: "Extracting",
  MATCHED: "Matched",
  MISMATCH: "Mismatch",
  NEEDS_REVIEW: "Needs review",
  DUPLICATE: "Duplicate",
  MISSING_TIMESHEET: "Missing timesheet",
  EXTRACTION_FAILED: "Extraction failed",
};

/**
 * One check's outcome. `REVIEW` is "verified, different, but plausible" (a rate
 * that changed, a partial period); `UNVERIFIABLE` is "one side is unknown".
 * Neither is ever a `MISMATCH`, which is how CINV-8 — null is never a mismatch —
 * is made structural rather than remembered.
 */
export const CHECK_RESULTS = [
  "MATCH",
  "MISMATCH",
  "REVIEW",
  "UNVERIFIABLE",
  "INFO",
] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];

/** The audit trail's vocabulary — transitions and the non-transition events around them. */
export const CONTRACTOR_INVOICE_EVENT_TYPES = [
  "SUBMITTED",
  "RESUBMITTED",
  "EXTRACTION_DONE",
  "EXTRACTION_FAILED",
  "EXTRACTION_RERUN",
  "FIELDS_CORRECTED",
  "VALIDATION_CHANGED",
  "CORRECTION_REQUESTED",
  "APPROVED",
  "REJECTED",
  /** Marked paid in one step, before payments were rows (CINV-23). Kept for the trail; no longer written. */
  "PAID",
  /** An admin tagged invoice lines to projects (CINV-20). */
  "LINES_TAGGED",
  /** A payment was recorded, changed or removed (CINV-23). The note says what, in words. */
  "PAYMENT_RECORDED",
  "PAYMENT_UPDATED",
  "PAYMENT_REMOVED",
  /** Closed as paid with a balance left, and the reason; and that close undone. */
  "CLOSED_SHORT",
  "CLOSE_UNDONE",
  /** An admin explained the flags on an invoice (CINV-27), and that note removed. */
  "EXPLAINED",
  "EXPLANATION_CLEARED",
  /** The approved hours fell below what the invoice was approved against (CINV-28). */
  "CHANGED_SINCE_APPROVAL",
  /** An admin filed the whole invoice under a project, and that project taken off (CINV-35). */
  "PROJECT_SET",
  "PROJECT_CLEARED",
] as const;
export type ContractorInvoiceEventType = (typeof CONTRACTOR_INVOICE_EVENT_TYPES)[number];

export const INVOICE_SORT_FIELDS = [
  "submittedAt",
  "period",
  "contractor",
  "total",
  "status",
  "validationStatus",
  "reviewedAt",
  "paidAt",
] as const;
export type InvoiceSortField = (typeof INVOICE_SORT_FIELDS)[number];

// --- Expense invoices (EXP) ---------------------------------------------------
// A supplier invoice or receipt an admin records against a project: no
// contractor, no month, no timesheet, and the document optional.

/**
 * An expense is money already spent, so recording it is paying it (EXP-7):
 * every live expense is PAID, and VOID is the delete. There is no approval.
 */
export const EXPENSE_INVOICE_STATUSES = ["PAID", "VOID"] as const;
export type ExpenseInvoiceStatus = (typeof EXPENSE_INVOICE_STATUSES)[number];

export const EXPENSE_INVOICE_STATUS_LABEL: Record<ExpenseInvoiceStatus, string> = {
  PAID: "Paid",
  VOID: "Void",
};

/**
 * What an expense could be before recording became paying (EXP-7). No row holds
 * one any more — the migration moved them to PAID — but the append-only event
 * trail still names them, so an event's from/to is this wider set.
 */
export const LEGACY_EXPENSE_INVOICE_STATUSES = ["RECORDED", "APPROVED"] as const;
export type ExpenseEventStatus = ExpenseInvoiceStatus | (typeof LEGACY_EXPENSE_INVOICE_STATUSES)[number];

export const EXPENSE_CATEGORIES = [
  "SOFTWARE",
  "HARDWARE",
  "TRAVEL",
  "FOOD_AND_DRINKS",
  "TRAINING",
  "SUBCONTRACTOR",
  "PROFESSIONAL_FEES",
  "OTHER",
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const EXPENSE_CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  SOFTWARE: "Software",
  HARDWARE: "Hardware",
  TRAVEL: "Travel",
  FOOD_AND_DRINKS: "Food & drinks",
  TRAINING: "Training",
  SUBCONTRACTOR: "Subcontractor",
  PROFESSIONAL_FEES: "Professional fees",
  OTHER: "Other",
};

/**
 * Where the read of an expense's document stands. `NO_DOCUMENT` is manual entry:
 * nothing was uploaded, so there was never a read to succeed or fail. Deliberately
 * not in INVOICE_EXTRACTION_STATUSES — a contractor invoice can never hold it,
 * and its CHECK stays the narrowest true statement (DATA-1).
 */
export const EXPENSE_EXTRACTION_STATUSES = [...INVOICE_EXTRACTION_STATUSES, "NO_DOCUMENT"] as const;
export type ExpenseExtractionStatus = (typeof EXPENSE_EXTRACTION_STATUSES)[number];

/**
 * A subset of the contractor verdicts, so every badge and renderer works
 * unchanged. There are no timesheets, so MISSING_TIMESHEET cannot happen.
 */
export const EXPENSE_VALIDATION_STATUSES = [
  "PENDING",
  "MATCHED",
  "MISMATCH",
  "NEEDS_REVIEW",
  "DUPLICATE",
  "EXTRACTION_FAILED",
] as const satisfies readonly InvoiceValidationStatus[];
export type ExpenseValidationStatus = (typeof EXPENSE_VALIDATION_STATUSES)[number];

export const EXPENSE_INVOICE_EVENT_TYPES = [
  "RECORDED",
  "FILE_ATTACHED",
  "FILE_REPLACED",
  "EXTRACTION_DONE",
  "EXTRACTION_FAILED",
  "EXTRACTION_RERUN",
  "FIELDS_CORRECTED",
  "UPDATED",
  "VALIDATION_CHANGED",
  "APPROVED",
  "PAID",
  "VOIDED",
] as const;
export type ExpenseInvoiceEventType = (typeof EXPENSE_INVOICE_EVENT_TYPES)[number];

/**
 * What an expense's document may be (EXP-9): a PDF, or a photo or screenshot of
 * a receipt. Decided by the bytes, never the declared type. A contractor
 * invoice stays a PDF (CINV-13).
 */
export const EXPENSE_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"] as const;
export type ExpenseDocumentType = (typeof EXPENSE_DOCUMENT_TYPES)[number];

export const EXPENSE_DOCUMENT_EXTENSION: Record<ExpenseDocumentType, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

export const EXPENSE_SORT_FIELDS = ["expenseDate", "supplier", "total", "status", "recordedAt"] as const;
export type ExpenseSortField = (typeof EXPENSE_SORT_FIELDS)[number];

// --- Invoice intake (CINV-21) ---------------------------------------------------
// A PDF an admin dropped on the inbox before saying whose it is or for which
// month. Not an invoice yet: filing it creates one.

export const INVOICE_INTAKE_STATUSES = ["PENDING_REVIEW", "FILED", "DISCARDED"] as const;
export type InvoiceIntakeStatus = (typeof INVOICE_INTAKE_STATUSES)[number];

/** How the intake arrived. Only uploads today; a mailbox would be a second. */
export const INVOICE_INTAKE_SOURCES = ["UPLOAD"] as const;
export type InvoiceIntakeSource = (typeof INVOICE_INTAKE_SOURCES)[number];

/** The most intakes one list request returns, and so the most one batch can hold. */
export const MAX_INTAKE_PAGE = 50;

/** What made a contractor a candidate for an uploaded invoice (CINV-22). */
export const CONTRACTOR_MATCH_REASONS = ["ABN", "GSTIN", "EMAIL", "NAME"] as const;
export type ContractorMatchReason = (typeof CONTRACTOR_MATCH_REASONS)[number];

/** The most PDFs one drop accepts: each is a paid read, and a drop is a batch a person reviews. */
export const MAX_FILES_PER_DROP = 20;

/**
 * How an expense came in (EXP-11): APP is the page — a drop, a photo, the form —
 * and EMAIL is a document attached to an admin's message to the expenses mailbox.
 */
export const EXPENSE_SOURCES = ["APP", "EMAIL"] as const;
export type ExpenseSource = (typeof EXPENSE_SOURCES)[number];

/** A message read from the expenses mailbox: claimed, recorded from, or turned away (EXP-11). */
export const EXPENSE_EMAIL_STATUSES = ["RECEIVED", "DONE", "REJECTED"] as const;
export type ExpenseEmailStatus = (typeof EXPENSE_EMAIL_STATUSES)[number];

/**
 * The inbox's project filter value for "filed under no project" (CINV-20).
 * Never a real id: those are cuids, lower-case.
 */
export const UNASSIGNED_PROJECT = "UNASSIGNED";

/** Sortable columns on the payment history. */
export const PAYMENT_SORT_FIELDS = ["paidAt", "contractor", "total"] as const;
export type PaymentSortField = (typeof PAYMENT_SORT_FIELDS)[number];

/**
 * What the model may say a document *is*. Never nullable on the wire — strict
 * JSON schemas travel better when enums carry an UNKNOWN member than when they
 * admit null (see `invoice-extraction.ts`).
 */
export const INVOICE_DOCUMENT_KINDS = [
  "INVOICE",
  "CREDIT_NOTE",
  "QUOTE",
  "RECEIPT",
  "TIMESHEET",
  "STATEMENT",
  "OTHER",
  "UNKNOWN",
] as const;
export type InvoiceDocumentKind = (typeof INVOICE_DOCUMENT_KINDS)[number];

/** A line's unit as printed. Superset of `RATE_UNITS`. */
export const EXTRACTED_UNITS = ["DAY", "HOUR", "OTHER", "UNKNOWN"] as const;
export type ExtractedUnit = (typeof EXTRACTED_UNITS)[number];

/** A currency as printed. Superset of `CURRENCIES`. */
export const EXTRACTED_CURRENCIES = ["AUD", "INR", "OTHER", "UNKNOWN"] as const;
export type ExtractedCurrency = (typeof EXTRACTED_CURRENCIES)[number];
